import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import {
    Tabs,
    Card,
    Form,
    Input,
    Row,
    Col,
    DatePicker,
    Checkbox,
    Button,
    Calendar,
} from "antd";
import {
    User,
    HeartPulse,
    Pencil,
    ClipboardList,
    Phone,
    Mail,
    MapPin,
    Save,
} from "lucide-react";

import dayjs from "dayjs";

import Layout from '../../Layout'

const { TextArea } = Input;

export default function HairClinicForm() {
  const { t } = useTranslation();
    const [form] = Form.useForm();

    const healthList = [
        t('hair_clinic.health_using_medicine'),
        t('hair_clinic.health_blood_clot'),
        t('hair_clinic.health_diabetes'),
        t('hair_clinic.health_hemorrhage'),
        t('hair_clinic.health_cardiovascular'),
        t('hair_clinic.health_hepatitis'),
        t('hair_clinic.health_kidney_liver'),
        t('hair_clinic.health_immune'),
        t('hair_clinic.health_thyroid'),
        t('hair_clinic.health_infection'),
        t('hair_clinic.health_anesthesia'),
        t('hair_clinic.health_epilepsy'),
        t('hair_clinic.health_pregnant'),
        t('hair_clinic.health_pacemaker'),
        t('hair_clinic.health_allergy'),
        t('hair_clinic.health_wound'),
        t('hair_clinic.health_blood_pressure'),
        t('hair_clinic.health_surgery'),
        t('hair_clinic.health_anemia'),
        t('hair_clinic.health_digestive'),
        t('hair_clinic.health_ovary'),
        t('hair_clinic.health_menstrual'),
    ];

    const hairList = [t('hair_clinic.hair_thin'), t('hair_clinic.hair_loss'), t('hair_clinic.hair_bald')];

    const items = [
        {
            key: "1",
            label: (
                <span className="flex items-center gap-2">
                    <User size={16} /> {t('hair_clinic.tab_general')}
                </span>
            ),

            children: (
                <Card className="shadow-sm dark:bg-[#1f1f1f] border-color-1f1f1f">
                    <Form layout="vertical" form={form} className="border-color-1f1f1f">
                        <Row gutter={16}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={t('hair_clinic.full_name')}
                                    name="name"
                                    rules={[{ required: true, message: t('hair_clinic.full_name_required') }]}
                                >
                                    <Input placeholder={t('hair_clinic.full_name_placeholder')} />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item label={t('hair_clinic.dob')} name="dob">
                                    <DatePicker className="w-full" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={t('hair_clinic.phone')}
                                    name="phone"
                                    rules={[{ required: true, message: t('hair_clinic.phone_required') }]}
                                >
                                    <Input prefix={<Phone size={16} />} placeholder="0909 xxx xxx" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item label={t('hair_clinic.email')} name="email">
                                    <Input prefix={<Mail size={16} />} placeholder="email@example.com" />
                                </Form.Item>
                            </Col>

                            <Col span={24}>
                                <Form.Item label={t('hair_clinic.address')} name="address">
                                    <Input prefix={<MapPin size={16} />} placeholder={t('hair_clinic.address_placeholder')} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Button
                            type="primary"
                            icon={<Save size={16} />}
                            className="mt-2"
                        >
                            {t('hair_clinic.save')}
                        </Button>
                    </Form>
                </Card>
            ),
        },

        {
            key: "2",
            label: (
                <span className="flex items-center gap-2">
                    <HeartPulse size={16} /> {t('hair_clinic.tab_health')}
                </span>
            ),

            children: (
                <Card className="shadow-sm dark:bg-[#1f1f1f] border-color-1f1f1f">
                    <div className="border rounded-xl p-5 bg-red-50 dark:bg-[#2a2a2a] border-color-1f1f1f">
                        <Checkbox.Group className="w-full">
                            <Row gutter={[16, 16]}>
                                {healthList.map((item, index) => (
                                    <Col xs={24} md={12} lg={8} key={index}>
                                        <Checkbox value={item}>{item}</Checkbox>
                                    </Col>
                                ))}
                            </Row>
                        </Checkbox.Group>
                    </div>

                    <Button type="primary" icon={<Save size={16} />} className="mt-6">
                        {t('hair_clinic.save')}
                    </Button>
                </Card>
            ),
        },

        {
            key: "3",
            label: (
                <span className="flex items-center gap-2">
                    <Pencil size={16} /> {t('hair_clinic.tab_hair')}
                </span>
            ),

            children: (
                <Card className="shadow-sm dark:bg-[#1f1f1f] border-color-1f1f1f">
                    <div className="border rounded-xl p-5 bg-orange-50 dark:bg-[#2a2a2a] border-color-1f1f1f">
                        <Checkbox.Group>
                            <Row gutter={[16, 16]}>
                                {hairList.map((item) => (
                                    <Col key={item}>
                                        <Checkbox value={item}>{item}</Checkbox>
                                    </Col>
                                ))}
                            </Row>
                        </Checkbox.Group>
                    </div>

                    <Button type="primary" icon={<Save size={16} />} className="mt-6">
                        {t('hair_clinic.save')}
                    </Button>
                </Card>
            ),
        },

        {
            key: "4",
            label: (
                <span className="flex items-center gap-2">
                    <ClipboardList size={16} /> {t('hair_clinic.tab_diagnosis')}
                </span>
            ),

            children: (
                <Card className="shadow-sm dark:bg-[#1f1f1f] border-color-1f1f1f">
                    <Form layout="vertical" className="border-color-1f1f1f">
                        <Form.Item label={t('hair_clinic.treatment_method')}>
                            <TextArea rows={4} />
                        </Form.Item>

                        <Button type="primary" icon={<Save size={16} />}>
                            {t('hair_clinic.save')}
                        </Button>
                    </Form>
                </Card>
            ),
        },
    ];

    return (
        <Layout>
            <Helmet>
                <title>{t('page.hair_clinic')} | Khothi</title>
            </Helmet>
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">

                {/* LEFT */}
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#3e91ab] flex items-center justify-center text-white">
                        <ClipboardList size={20} />
                    </div>

                    <div>
                        <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            {t('hair_clinic.page_title')}
                        </h1>

                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            {t('hair_clinic.page_subtitle')}
                        </p>
                    </div>
                </div>

                {/* RIGHT */}
                <div className="flex items-center gap-2">
                    <DatePicker
                        defaultValue={dayjs()}
                        format="DD/MM/YYYY"
                        className="min-w-[160px]"
                    />
                </div>

            </div>

            <Tabs items={items} />
        </Layout>
    );
}